import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormInput } from "@/components/post/FormBits";
import { LangWarn, wrongLang } from "@/lib/langCheck";

const EN_ONLY = ["bank_name", "account_holder"];

const FIELDS = [
  ["bank_name", "اسم البنك", "Bank name", 80],
  ["account_holder", "اسم صاحب الحساب", "Account holder name", 80],
  ["iban", "رقم الحساب المصرفي الدولي (IBAN)", "IBAN", 29, "ltr"],
  ["swift", "رمز السويفت (SWIFT)", "SWIFT code", 15, "ltr"],
  ["bank_address", "عنوان البنك", "Bank address", 160],
];
const clean = (v) => v.replace(/\s/g, "").toUpperCase();
const IBAN_OK = (v) => /^AE\d{21}$/.test(clean(v));
const IBAN_MSG = (L) => L("رقم الآيبان يجب أن يبدأ بـ AE ويليه 21 رقماً (مثال: AE070331234567890123456)", "IBAN must be AE followed by 21 digits (e.g. AE070331234567890123456)");

// Withdraw wallet balance -> POST /wallet/withdrawals (amount is held immediately, refunded if admin rejects).
export function WithdrawForm({ balance, lang, onClose, onSent }) {
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const [f, setF] = useState({ amount: "", bank_name: "", account_holder: "", iban: "", swift: "", bank_address: "" });
  const [busy, setBusy] = useState(false);
  const [save, setSave] = useState(true);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/wallet/bank-details").then(({ data }) => { if (data) { setSaved(true); setF((p) => ({ ...p, ...Object.fromEntries(FIELDS.map(([k]) => [k, data[k] || ""])) })); } }).catch(() => {}); }, []);
  const forget = async () => { await api.delete("/wallet/bank-details"); setSaved(false); setSave(false); toast.success(L("تم حذف بيانات الحساب المحفوظة", "Saved bank details removed")); };
  const amount = parseFloat(f.amount);
  const over = amount > balance;
  const send = async () => {
    if (!(amount > 0)) return toast.error(L("أدخل المبلغ المطلوب سحبه", "Enter the amount to withdraw"));
    if (over) return toast.error(L("الرصيد غير كافٍ", "Insufficient balance"));
    const empty = FIELDS.find(([k]) => !f[k].trim());
    if (empty) return toast.error(L(`${empty[1]} مطلوب`, `${empty[2]} is required`));
    if (EN_ONLY.some((k) => wrongLang(f[k], "en"))) return toast.error(L("اسم البنك واسم صاحب الحساب بالإنجليزية فقط", "Bank name and account holder must be in English only"));
    if (!IBAN_OK(f.iban)) return toast.error(IBAN_MSG(L));
    if (!/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(clean(f.swift))) return toast.error(L("رمز السويفت غير صحيح (8 أو 11 خانة)", "Invalid SWIFT code (8 or 11 characters)"));
    setBusy(true);
    try {
      await api.post("/wallet/withdrawals", { ...f, amount, iban: clean(f.iban), swift: clean(f.swift), save_details: save });
      toast.success(L("تم إرسال طلب السحب للإدارة وحجز المبلغ من محفظتك", "Withdrawal request sent; the amount is held from your wallet"));
      onSent();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir={lang === "ar" ? "rtl" : "ltr"} className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="withdraw-form">
        <DialogHeader><DialogTitle>{L("سحب الرصيد", "Withdraw balance")}</DialogTitle></DialogHeader>
        <label className="text-sm font-semibold">{L("الرصيد المطلوب سحبه (درهم)", "Amount to withdraw (AED)")} <span className="text-red-600">*</span>
          <FormInput className="mt-1" inputMode="decimal" dir="ltr" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(/[^0-9.]/g, "") })} testid="withdraw-amount" />
          <span className="block text-xs text-slate-500 mt-1" data-testid="withdraw-available">{L("الرصيد المتاح", "Available balance")}: <bdi dir="ltr">AED {Number(balance).toFixed(2)}</bdi></span>
          {over && <span className="block text-xs font-bold text-red-600 mt-1" data-testid="withdraw-insufficient">{L("الرصيد غير كافٍ، المبلغ أكبر من رصيدك المتاح", "Insufficient balance: amount exceeds your available balance")}</span>}
        </label>
        {FIELDS.map(([k, ar, en, max, dir]) => (
          <label key={k} className="text-sm font-semibold">{L(ar, en)} <span className="text-red-600">*</span>
            <FormInput className="mt-1" dir={EN_ONLY.includes(k) ? "ltr" : dir} maxLength={max} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} testid={`withdraw-${k}`} />
            {EN_ONLY.includes(k) && <LangWarn text={f[k]} lang="en" testid={`withdraw-${k}-lang-warning`} />}
            {k === "iban" && f.iban.trim() && !IBAN_OK(f.iban) && <p className="text-xs font-bold text-[#B91C1C] mt-1" data-testid="withdraw-iban-warning">{IBAN_MSG(L)}</p>}
          </label>
        ))}
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="w-4 h-4 accent-[#1A5C3A]" data-testid="withdraw-save-details" />
            {L("حفظ بيانات الحساب للمرات القادمة", "Save bank details for next time")}
          </label>
          {saved && <button type="button" onClick={forget} className="text-xs font-bold text-[#B91C1C] underline" data-testid="withdraw-forget-details">{L("حذف المحفوظة", "Remove saved")}</button>}
        </div>
        <div className="flex items-start gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-sm font-bold text-amber-900" data-testid="withdraw-timing-notice">
          <Info className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
          <span>{L("ملاحظة: سيتم إيداع المبلغ في حسابك من يوم إلى 5 أيام عمل بعد موافقة إدارة بازارو", "Note: The amount will be deposited to your account within 1 to 5 business days after Bazaroo admin approval")}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={send} disabled={busy || over} className="flex-1 h-11 rounded-xl bg-[#1A5C3A] disabled:opacity-60 text-white font-bold flex items-center justify-center gap-2" data-testid="withdraw-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{L("إرسال طلب السحب", "Send withdrawal request")}</button>
          <button onClick={onClose} className="px-5 h-11 rounded-xl border font-bold" data-testid="withdraw-cancel">{L("إلغاء", "Cancel")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
